import * as THREE from 'three'
import { VerletSystem } from '../sim/verlet.js'
import { NET } from '../constants.js'

// ============================================================================
//  Net — siatka kosza jako krata strun (diamentowy wzór) sterowana verletem.
//  Górny pierścień przypięty do obręczy; dół zwęża się. Przy trafieniu piłki
//  punkty dostają impuls -> naturalny "swish".
// ============================================================================

export class Net {
  constructor(rimCenter) {
    this.rimCenter = rimCenter.clone()
    this.cols = NET.SEGMENTS
    this.rows = NET.ROWS
    this.sys = new VerletSystem({
      gravity: new THREE.Vector3(0, -7, 0),
      damping: 0.94,
      iterations: 7,
    })

    this._buildPoints()
    this._buildConstraints()
    this._buildMesh()

    this._accum = 0
    this._fixed = 1 / 60
  }

  _idx(r, c) {
    return r * this.cols + ((c + this.cols) % this.cols)
  }

  _buildPoints() {
    const { cols, rows } = this
    for (let r = 0; r <= rows; r++) {
      const t = r / rows
      const radius = THREE.MathUtils.lerp(NET.TOP_RADIUS, NET.BOTTOM_RADIUS, t)
      const y = this.rimCenter.y - NET.LENGTH * t
      for (let c = 0; c < cols; c++) {
        const a = (c / cols) * Math.PI * 2
        const x = this.rimCenter.x + Math.cos(a) * radius
        const z = this.rimCenter.z + Math.sin(a) * radius
        this.sys.addPoint(x, y, z, r === 0) // górny rząd przypięty
      }
    }
  }

  _buildConstraints() {
    const { cols, rows } = this
    for (let r = 0; r <= rows; r++) {
      for (let c = 0; c < cols; c++) {
        // pierścień poziomy
        this.sys.addConstraint(this._idx(r, c), this._idx(r, c + 1))
        if (r < rows) {
          // przekątne (diamentowy splot)
          this.sys.addConstraint(this._idx(r, c), this._idx(r + 1, c))
          this.sys.addConstraint(this._idx(r, c), this._idx(r + 1, c + 1))
        }
      }
    }
  }

  _buildMesh() {
    const { cols, rows } = this
    // Pary indeksów wierzchołków rysowane jako linie. Rysujemy SAME przekątne
    // (diamentowy splot jak w prawdziwej siatce) + pierścień mocujący przy
    // obręczy; pierścienie pośrednie zostają tylko w fizyce (constraints).
    this._pairs = []
    for (let r = 0; r <= rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (r === 0) this._pairs.push([this._idx(r, c), this._idx(r, c + 1)])
        if (r < rows) {
          this._pairs.push([this._idx(r, c), this._idx(r + 1, c)])
          this._pairs.push([this._idx(r, c), this._idx(r + 1, c + 1)])
        }
      }
    }

    const positions = new Float32Array(this._pairs.length * 2 * 3)
    this.geo = new THREE.BufferGeometry()
    this.geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))

    // biały sznurek jak na zdjęciach produktowych D72
    const mat = new THREE.LineBasicMaterial({
      color: 0xf0f0f0,
      transparent: true,
      opacity: 0.9,
    })
    this.mesh = new THREE.LineSegments(this.geo, mat)
    this.mesh.name = 'Net'
    this.mesh.frustumCulled = false
    this._syncGeometry()
  }

  _syncGeometry() {
    const pos = this.geo.attributes.position.array
    const pts = this.sys.points
    let o = 0
    for (const [a, b] of this._pairs) {
      const pa = pts[a].pos
      const pb = pts[b].pos
      pos[o++] = pa.x; pos[o++] = pa.y; pos[o++] = pa.z
      pos[o++] = pb.x; pos[o++] = pb.y; pos[o++] = pb.z
    }
    this.geo.attributes.position.needsUpdate = true
    this.geo.computeBoundingSphere()
  }

  // Przenosi siatkę pod nową pozycję obręczy (kosz z innym wysięgiem): sztywna
  // translacja wszystkich punktów verleta o deltę — bez przebudowy geometrii.
  setRimCenter(v) {
    const delta = new THREE.Vector3().subVectors(v, this.rimCenter)
    if (delta.lengthSq() < 1e-12) return
    this.rimCenter.copy(v)
    for (const p of this.sys.points) {
      p.pos.add(delta)
      p.prev.add(delta)
    }
    this._syncGeometry()
  }

  // Impuls od przelatującej piłki — rozpycha pobliskie węzły w dół i na zewnątrz.
  punch(ballPos, strength = 1) {
    const pts = this.sys.points
    const c = this.rimCenter
    for (let i = this.cols; i < pts.length; i++) { // pomijamy przypięty górny rząd
      const p = pts[i]
      const dxz = Math.hypot(p.pos.x - ballPos.x, p.pos.z - ballPos.z)
      const fall = Math.max(0, 1 - dxz / 0.5)
      if (fall <= 0) continue
      const radial = new THREE.Vector3(p.pos.x - c.x, 0, p.pos.z - c.z).normalize()
      this.sys.addImpulse(i, new THREE.Vector3(
        radial.x * 0.06 * fall * strength,
        -0.14 * fall * strength,
        radial.z * 0.06 * fall * strength,
      ))
    }
  }

  update(dt) {
    // stały krok dla stabilności
    this._accum += Math.min(dt, 0.05)
    let steps = 0
    while (this._accum >= this._fixed && steps < 4) {
      this.sys.step(this._fixed)
      this._accum -= this._fixed
      steps++
    }
    this._syncGeometry()
  }
}
